import { create } from 'zustand';
import type { AddressDraft } from '@/domains/mobile-web/types/address.types';
import type { OtpChannel, RegistrationForm, VerificationPurpose } from '../types/auth.types';

export interface SentCode {
  purpose: VerificationPurpose;
  // E.164 (+62...)
  phone: string;
  channel: OtpChannel;
  // Date.now() before which no new code can be requested
  resendAt: number;
}

// Hand-over between the steps of registration and password reset. Memory only
// (never persisted): it holds a password, and a reload sends the user back to
// the first step. The verified phone number itself lives in the server session.
interface PendingAuthState {
  // Filled form waiting for its phone number to be verified (Buat Akun -> Verifikasi)
  registration: RegistrationForm | null;
  // Its address as picked, region names included, to fill Buat Akun again on the way back
  registrationAddress: AddressDraft | null;
  // Last code sent (Verifikasi, Lupa Password)
  sent: SentCode | null;
  // Lupa Password -> Password Baru: the code was accepted
  resetVerified: boolean;
}

interface PendingAuthActions {
  setRegistration: (form: RegistrationForm | null, address?: AddressDraft) => void;
  setSent: (sent: SentCode | null) => void;
  setResetVerified: (verified: boolean) => void;
  clear: () => void;
}

const initialState: PendingAuthState = {
  registration: null,
  registrationAddress: null,
  sent: null,
  resetVerified: false,
};

export const usePendingAuthStore = create<PendingAuthState & PendingAuthActions>()((set) => ({
  ...initialState,
  setRegistration: (registration, registrationAddress) =>
    set({ registration, registrationAddress: registrationAddress ?? null }),
  setSent: (sent) => set({ sent }),
  setResetVerified: (resetVerified) => set({ resetVerified }),
  clear: () => set(initialState),
}));
